import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Send, MessageSquare, User, Bot, Sparkles, X, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme, useBrand } from '../../context/ColorContext';

// Asistan cevabını güvenli şekilde zengin metne (markdown -> HTML) çevirir.
// Önce HTML kaçışı yapılır (XSS'e karşı), sonra sınırlı markdown biçimlendirmesi uygulanır.
const escapeHtml = (s) => String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const renderRichText = (raw) => {
    if (!raw) return '';
    let text = escapeHtml(raw)
        // satır içi: kalın, italik, kod, link
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>')
        .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-black/10 text-[10px]">$1</code>')
        .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="underline">$1</a>');

    const lines = text.split(/\r?\n/);
    let html = '';
    let listType = null; // 'ul' | 'ol'
    const closeList = () => { if (listType) { html += `</${listType}>`; listType = null; } };
    for (const line of lines) {
        const t = line.trim();
        if (t === '') { closeList(); continue; }
        let m;
        if ((m = t.match(/^#{1,6}\s+(.*)$/))) { closeList(); html += `<p class="font-black uppercase tracking-wide mt-2 mb-1">${m[1]}</p>`; continue; }
        if ((m = t.match(/^[-*•]\s+(.*)$/))) { if (listType !== 'ul') { closeList(); html += '<ul class="list-disc pl-4 space-y-1 my-1">'; listType = 'ul'; } html += `<li>${m[1]}</li>`; continue; }
        if ((m = t.match(/^\d+[.)]\s+(.*)$/))) { if (listType !== 'ol') { closeList(); html += '<ol class="list-decimal pl-4 space-y-1 my-1">'; listType = 'ol'; } html += `<li>${m[1]}</li>`; continue; }
        closeList();
        html += `<p class="my-1">${t}</p>`;
    }
    closeList();
    return html;
};

// Reactive mobile detection (chat becomes a full-screen portal on mobile)
const useIsMobile = (breakpoint = 1024) => {
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
    );
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < breakpoint);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [breakpoint]);
    return isMobile;
};

const Chatbot = ({ product, isOpen, onToggle }) => {
    const { theme } = useTheme();
    const { activeBrand } = useBrand();
    const isDark = theme === 'dark';
    const isMobile = useIsMobile();

    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isOpen]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;

        const userMsg = { role: 'user', content: input };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setIsLoading(true);

        try {
            const res = await fetch('/api/chatbot/ask', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    productId: product.id,
                    message: input
                })
            });
            const data = await res.json();
            if (data.error) throw new Error(data.error);

            setMessages(prev => [...prev, { role: 'bot', content: data.response }]);
        } catch (error) {
            toast.error('Mesaj iletilerken bir hata oluştu: ' + error.message);
            setMessages(prev => [...prev, { role: 'bot', content: 'Üzgünüm, şu an cevap veremiyorum. Lütfen daha sonra tekrar dene.' }]);
        } finally {
            setIsLoading(false);
        }
    };

    if (!product.chatbotEnabled) return null;

    // Shared chat panel (header + messages + input)
    const chatPanel = (
        <div className={`backdrop-blur-3xl rounded-[2.5rem] shadow-[0_30px_100px_-20px_rgba(0,0,0,0.3)] border overflow-hidden flex flex-col h-full ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/90 border-black/10'}`}>
            {/* Header */}
            <div className="px-6 py-5 flex items-center justify-between border-b border-black/5 dark:border-white/5 bg-white/5">
                <div className="flex items-center gap-3">
                    <div
                        className="w-2 h-2 rounded-full animate-pulse"
                        style={{ backgroundColor: activeBrand.primaryColor, boxShadow: `0 0 10px ${activeBrand.primaryColor}` }}
                    />
                    <div className="flex flex-col">
                        <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] ${isDark ? 'text-white/80' : 'text-slate-800'}`}>
                            ASİSTAN
                        </h3>
                        <span className={`text-[8px] font-bold opacity-40 uppercase tracking-tighter`}>{product.name}</span>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setMessages([])}
                        className={`p-2 rounded-xl transition-all hover:bg-black/5 dark:hover:bg-white/5 ${isDark ? 'text-white/20 hover:text-white/60' : 'text-slate-300 hover:text-slate-600'}`}
                        title="Sohbeti Temizle"
                    >
                        <MessageSquare size={14} />
                    </button>
                    <button
                        onClick={() => onToggle(false)}
                        className={`p-2 rounded-xl transition-all hover:bg-black/5 dark:hover:bg-white/5 ${isDark ? 'text-white/20 hover:text-white/60' : 'text-slate-300 hover:text-slate-600'}`}
                        title="Kapat"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* Messages Area */}
            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar"
            >
                {messages.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center text-center p-8 space-y-6">
                        <div className="relative">
                            <div
                                className="absolute inset-0 blur-3xl opacity-20 animate-pulse rounded-full"
                                style={{ backgroundColor: activeBrand.primaryColor }}
                            />
                            <div
                                className={`relative w-12 h-12 rounded-2xl flex items-center justify-center border ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-black/5 text-slate-800'}`}
                                style={{ color: activeBrand.primaryColor }}
                            >
                                <Bot size={24} />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <p className={`text-[10px] font-black uppercase tracking-widest opacity-40`}>Size nasıl yardımcı olabilirim?</p>
                        </div>
                        <div className="grid grid-cols-1 gap-2 w-full max-w-[240px]">
                            {['Nasıl uygulanır?', 'Sarfiyatı ne kadar?', 'Kuruma süresi nedir?'].map(q => (
                                <button
                                    key={q}
                                    onClick={() => { setInput(q); }}
                                    className={`px-4 py-3 rounded-2xl text-[10px] font-bold border transition-all active:scale-95 text-left ${isDark ? 'bg-white/5 border-white/10 text-white/40 hover:text-white/70 hover:bg-white/10' : 'bg-white border-black/5 text-slate-400 hover:text-slate-700 hover:border-black/10'}`}
                                >
                                    {q}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((msg, i) => (
                    <div
                        key={i}
                        className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'} gap-2 animate-in fade-in slide-in-from-bottom-2 duration-500`}
                    >
                        <div
                            className={`max-w-[85%] p-4 rounded-3xl text-[11px] leading-relaxed border transition-all
                            ${msg.role === 'user'
                                    ? 'font-bold text-white rounded-tr-none shadow-xl shadow-black/10'
                                    : (isDark ? 'bg-white/10 border-white/10 text-white/90 rounded-tl-none' : 'bg-black/5 border-black/5 text-slate-800 rounded-tl-none')}
                        `}
                            style={msg.role === 'user' ? { backgroundColor: activeBrand.primaryColor, borderColor: 'transparent' } : {}}
                        >
                            {msg.role === 'bot'
                                ? <div className="chat-rich [&_strong]:font-black [&_a]:font-bold" dangerouslySetInnerHTML={{ __html: renderRichText(msg.content) }} />
                                : msg.content}
                        </div>
                    </div>
                ))}

                {isLoading && (
                    <div className="flex items-start gap-2 animate-pulse">
                        <div className={`p-4 rounded-3xl rounded-tl-none border flex gap-1.5 ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'}`}>
                            <div className="w-1 h-1 bg-current opacity-30 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-1 h-1 bg-current opacity-30 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-1 h-1 bg-current opacity-30 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                    </div>
                )}
            </div>

            {/* Input Area */}
            <form
                onSubmit={handleSend}
                className={`p-4 flex gap-2 items-center border-t border-black/5 dark:border-white/5 bg-white/5`}
            >
                <div className="relative flex-1 group">
                    <input
                        autoFocus={isOpen}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Mesajınızı yazın..."
                        className={`w-full px-6 py-4 bg-transparent border-none outline-none focus:outline-none focus:ring-0 focus:border-none appearance-none rounded-2xl transition-all font-bold text-[11px] ${isDark ? 'text-white placeholder:text-white/20' : 'text-slate-900 placeholder:text-slate-300'}`}
                        style={{ WebkitAppearance: 'none', boxShadow: 'none' }}
                    />
                </div>
                <button
                    type="submit"
                    disabled={isLoading || !input.trim()}
                    className={`w-12 h-12 flex items-center justify-center transition-all hover:scale-110 active:scale-90 disabled:opacity-20`}
                    style={{ color: activeBrand.primaryColor }}
                >
                    <Send size={18} />
                </button>
            </form>
        </div>
    );

    return (
        <div className="w-full mt-8 relative z-40">
            {/* TOGGLE BAR — her zaman görünür; tıklayınca aşağı doğru açılır/kapanır (accordion) */}
            <button
                onClick={() => onToggle(!isOpen)}
                className="group flex items-center justify-between w-full px-8 py-5 rounded-[2rem] backdrop-blur-3xl border transition-all active:scale-[0.99] shadow-xl"
                style={{
                    backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.6)',
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                }}
            >
                <div className="flex items-center gap-4">
                    <div className="relative">
                        <div className="absolute inset-0 blur-lg opacity-40 animate-pulse rounded-full" style={{ backgroundColor: activeBrand.primaryColor }} />
                        <MessageSquare size={22} className="relative transition-transform group-hover:rotate-12" style={{ color: activeBrand.primaryColor }} />
                    </div>
                    <span className={`text-xs font-black uppercase tracking-[0.1em] text-left ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                        BU ÜRÜN HAKKINDA BİR SORUNUZ MU VAR?
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[9px] font-bold opacity-30 uppercase tracking-widest hidden sm:block">{isOpen ? 'KAPAT' : 'ASİSTANA SOR'}</span>
                    <ChevronDown size={18} className={`transition-transform duration-500 ${isOpen ? 'rotate-180' : ''}`} style={{ color: activeBrand.primaryColor }} />
                </div>
            </button>

            {/* INLINE — açıkken aşağı doğru açılır, akışta altındaki tüm içeriği aşağı iter (lightbox DEĞİL) */}
            {isOpen && (
                <div className="mt-4 animate-slide-down-in" style={{ height: 500 }}>
                    {chatPanel}
                </div>
            )}
        </div>
    );
};

export default Chatbot;
